import type { $Values } from 'utility-types'

import { Model } from 'modules/core/lib/mobx-rest'

export type Strategy = $Values<typeof PermissionAccessGroup.Strategies>
export type GroupableType = $Values<typeof PermissionAccessGroup.GroupableTypes>

interface IPermissionAccessGroup {
  id: number
  company_id: number
  permissions_group_id: number
  strategy: Strategy
  groupable_type: GroupableType | null
  groupable_id: number | null
  query_id: string | null
  /**
   * IDs of the users that are part of this group, this is only relevant for groups
   * that use the `members` strategy, for other types, it is an empty array.
   */
  members: number[]
  team_ids: []
  location_ids: []
  legal_entity_ids: []
}

class PermissionAccessGroup extends Model<IPermissionAccessGroup> {
  static urlRoot() {
    return '/permissions/access_groups'
  }

  static Strategies = {
    /**
     * Everyone in the company
     */
    everyone: 'everyone',
    /**
     * A "dynamic" group of people, i.e a Team or a Location.
     *
     * In this strategy, we use the `groupable_type` attribute to indicate the type of
     * entity that defines the group ("Team", "Location", etc...) and `groupable_id`
     * to store the id of the particular instance.
     */
    groupable: 'groupable',
    /**
     * An specific list of users.
     */
    members: 'members',
    /**
     * A customizable query to fetch accesses
     */
    fqlMultiselect: 'fqlmultiselect',
    fql: 'fql',
  } as const

  static GroupableTypes = {
    team: 'Team',
    location: 'Location',
    legalEntity: 'LegalEntity',
  } as const

  get isLocation(): boolean {
    return this.attributes.groupable_type === 'Location'
  }

  get isTeam(): boolean {
    return this.attributes.groupable_type === 'Team'
  }

  get isLegalEntity(): boolean {
    return this.attributes.groupable_type === 'LegalEntity'
  }

  get hasGroup(): boolean {
    return this.attributes.strategy === 'groupable'
  }

  get hasEveryone(): boolean {
    return this.attributes.strategy === 'everyone'
  }

  get hasManualSelection(): boolean {
    return this.attributes.strategy === 'members'
  }

  get teamIds(): number[] {
    return this.attributes.team_ids
  }

  get locationIds(): number[] {
    return this.attributes.location_ids
  }

  get legalEntityIds(): number[] {
    return this.attributes.legal_entity_ids
  }
}

export default PermissionAccessGroup
